import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Grid } from "@material-ui/core";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import i18n from "../../../i18nextInitiator";
import { connect } from "react-redux";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import { SET_TIMESTAMP } from "../../../actions/actionsTypes";
import moment from "jalali-moment";
import agent from "../../../agent";
import { setHours, setTiming } from "../../../actions/actionCreators";
import { Skeleton } from "@material-ui/lab";
import Divider from "../Divider";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "1rem",
    "& > *": {
      margin: ".8rem 0",
    },
    "& button": {
      boxShadow: "unset",
    },
    "& *:focus": {
      outline: 0,
    },
  },
  paper: {
    boxShadow: "unset",
    display: "flex",
    flexDirection: "row-reverse",
  },
  tabItem: {
    width: "22%",
    minWidth: "unset",
  },
  hide: { display: "none" },
  hourItem: {
    width: 100,
    height: 40,
    margin: "0.5rem",
  },
  hourItemSelected: {
    color: theme.palette.primary.main,
    backgroundColor: `${theme.palette.primary.light} !important`,
    borderColor: theme.palette.primary.main,
  },
  inexpensive: {
    color: theme.palette.success.main,
    borderColor: theme.palette.success.main,
  },
  primary: {
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
  },
  dot: {
    width: "1rem",
    height: "1rem",
    borderRadius: "50%",
    marginRight: ".2rem",
  },
  dotInexpensive: {
    backgroundColor: theme.palette.success.main,
  },
  dotPrimary: {
    backgroundColor: theme.palette.primary.main,
  },
  dotInactive: {
    backgroundColor: theme.palette.text.secondary,
  },
}));

const t = i18n.getFixedT("fa", "general");

const mapStateToProps = (state) => ({ ...state.order });

const mapDispatchToProps = (dispatch) => ({
  setTimestamp: (time) => dispatch({ type: SET_TIMESTAMP, time }),
});

const TimePicker = (props) => {
  const classes = useStyles(props);
  const [selectedDay, setSelectedDay] = useState(0);
  const [selectedTime, setSelectedTime] = useState(undefined);
  const activeHourRef = useRef();

  useEffect(() => {
    if (props.order.time && props.timing.length > 0) {
      const selectedMoment = moment.unix(props.order.time);
      const dayIndex = props.timing.findIndex((time) =>
        selectedMoment.isSame(moment(time.date, "YYYY-MM-DD"), "day")
      );
      if (dayIndex !== -1) {
        setSelectedDay(dayIndex);
        const timeIndex = props.timing[dayIndex].periods.findIndex(
          (period) => period.timestamp === props.order.time
        );
        if (timeIndex !== -1) {
          setSelectedTime(props.order.time);
          setHours(props.timing[dayIndex].periods);
        }
      }
    }
  }, [props.order.time, props.timing]);

  const handleChangeDay = (event, newValue) => {
    setSelectedDay(newValue);
    setSelectedTime(undefined);
    setHours(props.timing[newValue].periods);
  };

  const handleChangeHour = (newValue) => {
    setSelectedTime(newValue);
  };

  useEffect(() => {
    if (activeHourRef.current) activeHourRef.current.scrollIntoView();
    agent.Order.timing(props.order.car_id).then((res) => {
      if (res && res.success) setTiming(res.data);
      return res;
    });
  }, [props.order.car_id]);

  const submitTime = () => {
    if (!selectedTime) {
      return;
    }
    props.setTimestamp(selectedTime);
    props.close();
  };

  const daysMapper = props.timing
    ? props.timing.map((time, index) => {
        const day = moment(time.date, "YYYY-MM-DD");
        const selected = index === selectedDay;
        return (
          <Tab
            key={time.date}
            value={index}
            className={
              selected
                ? `${classes.primary} ${classes.tabItem}`
                : time.inexpensive
                ? `${classes.inexpensive} ${classes.tabItem}`
                : `${classes.tabItem}`
            }
            disabled={!time.available}
            label={
              <>
                <Typography style={{ padding: "0 1rem" }}>
                  {day.locale("fa").format("dddd")}
                </Typography>
                <Typography>{day.format("D MMMM")}</Typography>
              </>
            }
            style={{
              minWidth: "-webkit-min-content",
              "-webkit-appearance": "none",
              "-webkit-flex": "1 1 100%",
              padding: ".5rem 1rem",
            }}
          />
        );
      })
    : Array(6)
        .fill("")
        .map((time, index) => {
          return (
            <Tab
              key={index}
              value={index}
              className={classes.tabItem}
              label={
                <>
                  <Skeleton width={"4rem"} />
                  <Skeleton width={"4rem"} />
                </>
              }
            />
          );
        });

  const hoursMapper = props.hours
    ? props.hours.map((hour, index) => {
        const selected = selectedTime === hour.timestamp;
        const timeoutToOrder = 2;
        const isPassed =
          selectedDay === 0 &&
          Number(moment().format("H")) + timeoutToOrder > hour.title;
        if (!isPassed)
          return (
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              item
              xs={4}
              md={3}
              key={hour.timestamp}
            >
              <Button
                value={hour.timestamp}
                className={
                  selected
                    ? `${classes.hourItem} ${classes.hourItemSelected}`
                    : hour.inexpensive
                    ? `${classes.hourItem} ${classes.inexpensive}`
                    : classes.hourItem
                }
                selected={selectedTime === hour.timestamp}
                onClick={() => handleChangeHour(hour.timestamp)}
                disabled={!hour.available}
                variant={"outlined"}
              >
                <Typography>{`${t("hour")} ${hour.title}`}</Typography>
              </Button>
            </Grid>
          );
      })
    : Array(6)
        .fill("")
        .map((hour, index) => {
          return (
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              item
              xs={4}
              md={3}
              key={index}
            >
              <Button className={classes.hourItem} variant={"outlined"}>
                <Skeleton width={"4rem"} />
              </Button>
            </Grid>
          );
        });

  return (
    <>
      <Grid container justifyContent={"flex-start"}>
        <Grid item>
          <Grid
            container
            justifyContent={"flex-start"}
            style={{ alignItems: "center" }}
          >
            <Grid item>
              <div className={`${classes.dot} ${classes.dotInexpensive}`} />
            </Grid>
            <Grid>
              <Typography className={classes.inexpensive}>
                {t("time.inexpensive")}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Paper square className={classes.paper}>
          <Tabs
            value={selectedDay}
            onChange={handleChangeDay}
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons="on"
            orientation="horizontal"
            style={{
              justifyContent: "space-around",
              width: "100%",
              "-webkit-appearance": "none",
            }}
            aria-label="scrollable auto tabs example"
          >
            {daysMapper}
          </Tabs>
        </Paper>
      </Grid>
      <Divider />
      <Grid container>{hoursMapper}</Grid>

      <Button
        onClick={submitTime}
        size={"large"}
        variant="contained"
        color={"primary"}
        fullWidth
      >
        {t("submit")} {t("time.picker-title")}
      </Button>
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(TimePicker);
